'use client';

import React from 'react';
import {
  Banknote,
  ArrowRightLeft,
  CreditCard,
  QrCode,
  ExternalLink,
  Ticket,
  MoreHorizontal,
  Check
} from 'lucide-react';

export interface PaymentMethodItem {
  id: string;
  type: string;
  name: string;
  enabled: boolean;
  requiresDeposit?: boolean;
  depositAmount?: number;
  bankAlias?: string;
  bankHolder?: string;
  bankCbu?: string;
  roundBalance?: boolean;
}

interface PaymentMethodsGridProps {
  methods: PaymentMethodItem[];
  mpConnected: boolean;
  onToggleMethod: (methodId: string, currentEnabled: boolean) => void;
}

export function PaymentMethodsGrid({
  methods,
  mpConnected,
  onToggleMethod
}: PaymentMethodsGridProps) {
  // Configuración visual de cada tipo
  const methodConfigs: Record<string, { label: string; desc: string; icon: any }> = {
    EFECTIVO: {
      label: 'Efectivo',
      desc: 'Pago en mano al asistir',
      icon: Banknote
    },
    TRANSFERENCIA: {
      label: 'Transferencia',
      desc: 'Alias / CBU mostrado en recepción',
      icon: ArrowRightLeft
    },
    CREDITO: {
      label: 'Tarjeta crédito',
      desc: 'Posnet / Lapos',
      icon: CreditCard
    },
    DEBITO: {
      label: 'Tarjeta débito',
      desc: 'Posnet / Lapos',
      icon: CreditCard
    },
    MERCADOPAGO: {
      label: 'MercadoPago QR',
      desc: mpConnected ? 'Cobro en el local' : 'Conectá MercadoPago para habilitar',
      icon: QrCode
    },
    MODO: {
      label: 'MercadoPago Checkout',
      desc: mpConnected ? 'Cobro remoto de seña' : 'Conectá MercadoPago para habilitar',
      icon: ExternalLink
    },
    CUENTA_CORRIENTE: {
      label: 'Voucher',
      desc: 'Aplicar voucher emitido',
      icon: Ticket
    },
    OTRO: {
      label: 'Otro',
      desc: 'Cualquier método no listado',
      icon: MoreHorizontal
    }
  };

  const enabledCount = methods.filter((m) => m.enabled).length;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            MÉTODOS HABILITADOS
          </h3>
          <span className="text-xs font-bold text-slate-500">
            {enabledCount}/{methods.length || 8}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Aparecen como opciones al cobrar un turno o emitir un voucher.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {methods.map((method) => {
          const cfg = methodConfigs[method.type] || {
            label: method.name,
            desc: 'Método configurado',
            icon: CreditCard
          };
          const Icon = cfg.icon;
          const isMpMethod = method.type === 'MERCADOPAGO' || method.type === 'MODO';
          const isDisabled = isMpMethod && !mpConnected;

          return (
            <div
              key={method.id}
              onClick={() => {
                if (!isDisabled) {
                  onToggleMethod(method.id, method.enabled);
                }
              }}
              className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                isDisabled
                  ? 'bg-slate-50/70 border-slate-200/80 opacity-60 cursor-not-allowed'
                  : method.enabled
                  ? 'bg-white border-emerald-400/80 shadow-2xs hover:border-emerald-500 cursor-pointer'
                  : 'bg-white border-slate-200/90 hover:border-slate-300 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                    method.enabled && !isDisabled
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-400 border-slate-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>

                <div className="min-w-0">
                  <h4 className="text-xs font-bold text-slate-900 truncate">
                    {cfg.label}
                  </h4>
                  <p className="text-[11px] text-slate-400 truncate">
                    {cfg.desc}
                  </p>
                </div>
              </div>

              {/* Checkbox indicator */}
              <div
                className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all shrink-0 ${
                  isDisabled
                    ? 'border-slate-300 bg-slate-100 text-transparent'
                    : method.enabled
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'border-slate-300 bg-white text-transparent'
                }`}
              >
                <Check className="w-3.5 h-3.5 stroke-[3]" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
